"use client";

import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { useEffect } from "react";

const TAB_STORAGE_LIMIT: number = 20;

export type AtomTabType = {
	id: string;
	name: string;
};

const atomsCache = new Map<
	string,
	ReturnType<typeof atomWithStorage<AtomTabType[]>>
>();

const getTabsAtom = (tabKey: string) => {
	if (!atomsCache.has(tabKey)) {
		atomsCache.set(tabKey, atomWithStorage(tabKey, [] as AtomTabType[]));
	}
	return atomsCache.get(tabKey)!;
};

const saveTabsToLocalStorage = (value: AtomTabType[], tabKey: string) => {
	localStorage.setItem(tabKey, JSON.stringify(value));
};

const loadTabsFromLocalStorage = (tabKey: string) => {
	const item = localStorage.getItem(tabKey);
	if (item) {
		return JSON.parse(item);
	}
	return [];
};

export const useTabs = (tabKey: string) => {
	const tabsAtom = getTabsAtom(tabKey);
	const [tabs, setTabs] = useAtom(tabsAtom);
	useEffect(() => {
		const storedTabs = loadTabsFromLocalStorage(tabKey);
		if (storedTabs.length > 0) {
			setTabs(storedTabs);
			saveTabsToLocalStorage(storedTabs, tabKey);
		}
	}, [tabKey, setTabs]);

	const customSetTabs = (newTabs: AtomTabType[]) => {
		const limitedTabs =
			newTabs.length > TAB_STORAGE_LIMIT
				? newTabs.slice(-TAB_STORAGE_LIMIT)
				: newTabs;

		setTabs(limitedTabs);
	};

	return [tabs, customSetTabs] as const;
};
